/* eslint-disable no-unused-vars, no-undef */
(() => {
  'use strict'

  // ──────────────────────────────────────────────────────────────────────────────
  // 1. TENTBOX NAMESPACE INITIALIZATION
  // ──────────────────────────────────────────────────────────────────────────────
  const Tentbox = window.Tentbox = window.Tentbox || {}
  Tentbox.canPlayVideo = true
  Tentbox.selectors = Tentbox.selectors || {}
  // (Tentbox.selectors.marketing.klaviyoFlyout is expected to be set elsewhere.)

  // ──────────────────────────────────────────────────────────────────────────────
  // 2. FOCUSABLE ELEMENTS & FOCUS TRAP
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Return an array of all focusable descendants within `container`.
   * Used for trapping focus in modals/dialogs.
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  const getFocusableElements = (container) => {
    const selector = [
      'summary',
      'a[href]',
      'button:enabled',
      '[tabindex]:not([tabindex^="-"])',
      '[draggable]',
      'area',
      'input:not([type=hidden]):enabled',
      'select:enabled',
      'textarea:enabled',
      'object',
      'iframe',
    ].join(', ')
    return Array.from(container.querySelectorAll(selector))
  }

  /**
   * Close an open <details> (if any) when Escape is pressed inside it.
   * @param {KeyboardEvent} event
   */
  const onKeyUpEscape = (event) => {
    if (event.code !== 'Escape') return
    const openDetails = event.target.closest('details[open]')
    if (!openDetails) return

    const summaryEl = openDetails.querySelector('summary')
    openDetails.removeAttribute('open')
    summaryEl?.setAttribute('aria-expanded', 'false')
    summaryEl?.focus()
  }

  /**
   * Trap keyboard focus inside `container`. After calling, focus will be set to `elementToFocus`.
   * Call `removeTrapFocus()` to release.
   * @param {HTMLElement} container
   * @param {HTMLElement} [elementToFocus=container]
   */
  const trapFocusHandlers = { focusin: null, focusout: null, keydown: null }
  const trapFocus = (container, elementToFocus = container) => {
    const elements = getFocusableElements(container)
    if (elements.length === 0) {
      elementToFocus?.focus({ preventScroll: true })
      return
    }

    const firstElem = elements[0]
    const lastElem = elements[elements.length - 1]

    // Remove any existing trap so we don't double-bind
    removeTrapFocus()

    trapFocusHandlers.focusin = (evt) => {
      if (
        evt.target !== container &&
        evt.target !== firstElem &&
        evt.target !== lastElem
      ) {
        return
      }
      document.addEventListener('keydown', trapFocusHandlers.keydown)
    }

    trapFocusHandlers.focusout = () => {
      document.removeEventListener('keydown', trapFocusHandlers.keydown)
    }

    trapFocusHandlers.keydown = (evt) => {
      if (evt.code !== 'Tab') return
      const active = document.activeElement
      // If on last focusable and tab forward → go to first
      if (active === lastElem && !evt.shiftKey) {
        evt.preventDefault()
        firstElem.focus({ preventScroll: true })
      }
      // If on first (or container) and shift+tab → go to last
      if ((active === container || active === firstElem) && evt.shiftKey) {
        evt.preventDefault()
        lastElem.focus({ preventScroll: true })
      }
    }

    document.addEventListener('focusin', trapFocusHandlers.focusin)
    document.addEventListener('focusout', trapFocusHandlers.focusout)

    elementToFocus.focus({ preventScroll: true })
  }

  /**
   * Remove any existing focus trap. Optionally re-focus on `elementToFocus`.
   * @param {HTMLElement|null} [elementToFocus=null]
   */
  const removeTrapFocus = (elementToFocus = null) => {
    document.removeEventListener('focusin', trapFocusHandlers.focusin)
    document.removeEventListener('focusout', trapFocusHandlers.focusout)
    document.removeEventListener('keydown', trapFocusHandlers.keydown)

    if (elementToFocus) {
      elementToFocus.focus({ preventScroll: true })
    }
  }

  // Expose these as globals for backward compatibility (cart.js, reusable-modal.js, etc.)
  window.getFocusableElements = getFocusableElements
  window.onKeyUpEscape = onKeyUpEscape
  window.trapFocus = trapFocus
  window.removeTrapFocus = removeTrapFocus

  Tentbox.trapFocus = trapFocus
  Tentbox.removeTrapFocus = removeTrapFocus

  // ──────────────────────────────────────────────────────────────────────────────
  // 3. BANDWIDTH DETECTION
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Enhanced bandwidth and device capability detection optimized for mobile devices.
   * Checks multiple indicators: connection type, downlink speed, save-data preference, and device memory.
   */
  const determineBandwidth = () => {
    try {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
      let shouldDisableVideo = false
      const reasons = []

      // Check if user has enabled data saver mode
      if (navigator.connection?.saveData) {
        shouldDisableVideo = true
        reasons.push('save-data preference enabled')
      }

      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        shouldDisableVideo = true
        reasons.push('prefers-reduced-motion')
      }

      // Check effective connection type (more reliable on mobile than downlink)
      if (connection?.effectiveType) {
        const effectiveType = connection.effectiveType

        // Disable video on slow connections
        if (effectiveType === 'slow-2g' || effectiveType === '2g') {
          shouldDisableVideo = true
          reasons.push(`slow connection type: ${effectiveType}`)
        }

        // Be conservative with 3g on mobile
        if (effectiveType === '3g' && isMobileDevice()) {
          shouldDisableVideo = true
          reasons.push('3g connection on mobile device')
        }
      }

      // Fallback to downlink speed if effectiveType not available
      if (connection?.downlink) {
        const downlink = connection.downlink

        // More conservative thresholds for mobile
        const threshold = isMobileDevice() ? 1.5 : 1.0
        if (downlink < threshold) {
          shouldDisableVideo = true
          reasons.push(`low downlink speed: ${downlink}Mbps (threshold: ${threshold}Mbps)`)
        }
      }

      // Check device memory (helps prevent crashes on low-end devices)
      if (navigator.deviceMemory) {
        if (navigator.deviceMemory < 2) {
          shouldDisableVideo = true
          reasons.push(`low device memory: ${navigator.deviceMemory}GB`)
        }
      }

      // Check if device is likely low-powered based on CPU cores
      if (isMobileDevice() && navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4) {
        shouldDisableVideo = true
        reasons.push(`low CPU cores on mobile: ${navigator.hardwareConcurrency}`)
      }

      Tentbox.canPlayVideo = !shouldDisableVideo
    } catch (error) {
      // Conservative fallback - disable on mobile if we can't detect
      if (isMobileDevice()) {
        Tentbox.canPlayVideo = false
        console.log('Video disabled as fallback on mobile device')
      }
    }
  }

  /**
   * Simple mobile device detection
   */
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.matchMedia('(max-width: 768px)').matches
  }

  // Initial check
  determineBandwidth()

  // Re-check when connection changes (mobile users often switch between WiFi/cellular)
  if (navigator.connection) {
    let reevalTimeout
    navigator.connection.addEventListener('change', () => {
      if (reevalTimeout) clearTimeout(reevalTimeout)
      reevalTimeout = setTimeout(() => {
        // console.log('Connection changed, re-evaluating video capabilities')
        determineBandwidth()
      }, 300)
    })
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // 4. <details> SUMMARY ACCESSIBILITY ENHANCEMENTS
  // ──────────────────────────────────────────────────────────────────────────────
  document.querySelectorAll('[id^="Details-"] summary').forEach(summary => {
    summary.setAttribute('role', 'button')
    summary.setAttribute(
      'aria-expanded',
      summary.parentNode.hasAttribute('open'),
    )

    const sibling = summary.nextElementSibling
    if (sibling?.id) {
      summary.setAttribute('aria-controls', sibling.id)
    }

    summary.addEventListener('click', () => {
      const isOpen = summary.closest('details').hasAttribute('open')
      summary.setAttribute('aria-expanded', String(!isOpen))
    })

    // Attach Escape‐key handler unless inside <header-drawer>
    if (!summary.closest('header-drawer')) {
      summary.parentElement.addEventListener('keyup', onKeyUpEscape)
    }
  })

  // ──────────────────────────────────────────────────────────────────────────────
  // 5. FOCUS-VISIBLE POLYFILL (FOR BROWSERS MISSING :focus-visible)
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * If the browser does NOT support :focus-visible, install a simple polyfill
   * that adds a “.focused” class each time a keyboard nav key is used.
   */
  const focusVisiblePolyfill = () => {
    const NAV_KEYS = new Set([
      'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
      'Tab', 'Enter', 'Space', 'Escape', 'Home', 'End', 'PageUp', 'PageDown',
    ])
    let lastInputWasMouse = false
    let currentFocusedElem = null

    window.addEventListener('keydown', (e) => {
      if (NAV_KEYS.has(e.code)) {
        lastInputWasMouse = false
      }
    })

    window.addEventListener('mousedown', () => {
      lastInputWasMouse = true
    })

    window.addEventListener(
      'focus',
      () => {
        currentFocusedElem?.classList.remove('focused')
        if (!lastInputWasMouse) {
          currentFocusedElem = document.activeElement
          currentFocusedElem?.classList.add('focused')
        }
      },
      true, // useCapture to catch all focus events
    )
  }

  // Test for :focus-visible support; if missing, load the polyfill
  try {
    document.querySelector(':focus-visible')
  } catch (_) {
    focusVisiblePolyfill()
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // 6. PAUSE ALL EMBEDDED MEDIA
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Send postMessage to all YouTube/Vimeo iframes on the page to pause playback.
   */
  const pauseAllMedia = () => {
    document.querySelectorAll('.js-youtube').forEach(ytIframe => {
      ytIframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        '*',
      )
    })
    document.querySelectorAll('.js-vimeo').forEach(vimeoIframe => {
      vimeoIframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*')
    })
  }
  Tentbox.pauseAllMedia = pauseAllMedia

  // ──────────────────────────────────────────────────────────────────────────────
  // 7. DEBOUNCE UTILITY
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Return a debounced version of `fn` that only executes at most once every `wait` ms.
   * @param {Function} fn
   * @param {number}   wait
   * @returns {Function}
   */
  const debounce = (fn, wait) => {
    let timeoutId = null
    return function (...args) {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => fn.apply(this, args), wait)
    }
  }
  Tentbox.debounce = debounce

  // Expose debounce globally for backward compatibility
  window.debounce = debounce

  // ──────────────────────────────────────────────────────────────────────────────
  // 8. FETCH CONFIGURATION HELPER
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Return a basic fetch config with JSON headers; can override response type.
   * @param {string} [type='json']
   * @returns {object}
   */
  const fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  })
  Tentbox.fetchConfig = fetchConfig

  // Expose fetchConfig globally for backward compatibility
  window.fetchConfig = fetchConfig

  // ──────────────────────────────────────────────────────────────────────────────
  // 9. SHOPIFY COMMONS
  // ──────────────────────────────────────────────────────────────────────────────
  window.Shopify = window.Shopify || {}

  /**
   * Bind function `fn` to scope `scope`.
   * @param {Function} fn
   * @param {object}   scope
   * @returns {Function}
   */
  Shopify.bind = (fn, scope) => (...args) => fn.apply(scope, args)

  /**
   * Set a <select> element’s selectedIndex to the option matching `value`.
   * @param {HTMLSelectElement} selectEl
   * @param {string}            value
   * @returns {number|null}
   */
  Shopify.setSelectorByValue = (selectEl, value) => {
    for (let i = 0; i < selectEl.options.length; i++) {
      const opt = selectEl.options[i]
      if (opt.value === value || opt.innerHTML === value) {
        selectEl.selectedIndex = i
        return i
      }
    }
    return null
  }

  /**
   * Attach an event listener (cross‐browser).
   * @param {Element|Window|Document} target
   * @param {string}                  eventName
   * @param {Function}                callback
   */
  Shopify.addListener = (target, eventName, callback) => {
    if (typeof target.addEventListener === 'function') {
      target.addEventListener(eventName, callback, false)
    } else {
      target.attachEvent(`on${eventName}`, callback)
    }
  }

  /**
   * Programmatically create and submit a form to `path`.
   * @param {string} path
   * @param {object} [options={}]
   *   - method: HTTP method (default 'post')
   *   - parameters: { key: value, ... } to send as hidden inputs
   */
  Shopify.postLink = (path, options = {}) => {
    const method = options.method || 'post'
    const params = options.parameters || {}
    const form = document.createElement('form')
    form.method = method
    form.action = path

    Object.entries(params).forEach(([key, val]) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = key
      input.value = val
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)
  }

  /**
   * CountryProvinceSelector:
   * Auto‐populates a “province” <select> based on chosen “country”.
   * Expects each `<option>` in countryEl to have `data-provinces='[["CA","California"], ...]'`.
   * @param {string} country_domid
   * @param {string} province_domid
   * @param {object} options
   *   - hideElement: id to hide if no provinces exist
   */
  Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
    this.countryEl = document.getElementById(country_domid)
    this.provinceEl = document.getElementById(province_domid)
    const hideEl = (options && options.hideElement) ? options.hideElement : province_domid
    this.provinceContainer = document.getElementById(hideEl)

    Shopify.addListener(
      this.countryEl,
      'change',
      Shopify.bind(this.countryHandler, this),
    )

    this.initCountry()
    this.initProvince()
  }

  Shopify.CountryProvinceSelector.prototype = {
    /**
     * Initialize country select based on data-default, then populate provinces.
     */
    initCountry () {
      const defaultVal = this.countryEl.getAttribute('data-default')
      Shopify.setSelectorByValue(this.countryEl, defaultVal)
      this.countryHandler()
    },

    /**
     * Initialize province select based on data-default.
     */
    initProvince () {
      const defaultVal = this.provinceEl.getAttribute('data-default')
      if (defaultVal && this.provinceEl.options.length > 0) {
        Shopify.setSelectorByValue(this.provinceEl, defaultVal)
      }
    },

    /**
     * Handler when country changes: parse its data-provinces JSON, populate <select> or hide if none.
     * @param {Event} e
     */
    countryHandler (e) {
      const selectedOpt = this.countryEl.options[this.countryEl.selectedIndex]
      const rawProvinces = selectedOpt.getAttribute('data-provinces')
      let provinces = []

      try {
        provinces = JSON.parse(rawProvinces)
      } catch (_) {
        provinces = []
      }

      // Remove existing <option> children
      while (this.provinceEl.firstChild) {
        this.provinceEl.removeChild(this.provinceEl.firstChild)
      }

      if (!provinces || provinces.length === 0) {
        this.provinceContainer.style.display = 'none'
      } else {
        provinces.forEach(([value, label]) => {
          const opt = document.createElement('option')
          opt.value = value
          opt.innerHTML = label
          this.provinceEl.appendChild(opt)
        })
        this.provinceContainer.style.display = ''
      }
    },
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // 10. QUANTITY-INPUT CUSTOM ELEMENT
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * <quantity-input>
   * Wraps an <input type="number"> with “+” / “−” buttons to increment/decrement.
   * Fires a “change” event only if the value actually changed.
   */
  class QuantityInput extends HTMLElement {
    constructor () {
      super()
      this.input = this.querySelector('input')
      this.changeEvent = new Event('change', { bubbles: true })

      this.querySelectorAll('button').forEach(button => {
        button.addEventListener('click', (evt) => this.onButtonClick(evt))
      })
    }

    onButtonClick (event) {
      event.preventDefault()
      const prev = this.input.value
      if (event.currentTarget.name === 'plus') {
        this.input.stepUp()
      } else {
        this.input.stepDown()
      }
      if (this.input.value !== prev) {
        this.input.dispatchEvent(this.changeEvent)
      }
    }
  }
  customElements.define('quantity-input', QuantityInput)

  // ──────────────────────────────────────────────────────────────────────────────
  // 11. DEFERRED-MEDIA CUSTOM ELEMENT
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * <deferred-media>
   * Shows a “poster” element (id starts “Deferred-Poster-”); on click, loads <iframe> or <video> from a <template>.
   * If a Vimeo iframe exists, fetches its thumbnail via Vimeo’s oEmbed API first.
   */
  class DeferredMedia extends HTMLElement {
    constructor () {
      super()
      const poster = this.querySelector('[id^="Deferred-Poster-"]')
      if (!poster) return
      poster.addEventListener('click', () => this.loadContent())

      // Preload Vimeo thumbnail if necessary
      const template = this.querySelector('template')
      const vimeoIframe = template?.content.querySelector('iframe.js-vimeo')
      if (vimeoIframe) {
        const match = vimeoIframe.src.match(/video\/(\d+)/)
        const videoId = match?.[1]
        if (videoId) {
          this.loadVimeoThumbnail(videoId, poster)
        }
      }
    }

    /**
     * Fetch Vimeo’s oEmbed JSON, extract and insert a high-res thumbnail <img>.
     * @param {string} videoId
     * @param {HTMLElement} container
     */
    async loadVimeoThumbnail (videoId, container) {
      try {
        const resp = await fetch(
          `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}&width=1280`,
        )
        if (!resp.ok) throw new Error('Network response was not ok')
        const data = await resp.json()
        if (!data.thumbnail_url) throw new Error('No thumbnail URL in response')

        // Upgrade thumbnail to 1280×720
        const highRes = data.thumbnail_url.replace(/\d+x\d+/, '1280x720')
        const img = document.createElement('img')
        img.src = highRes
        img.className = 'w-full h-full object-cover'
        img.loading = 'lazy'
        img.width = 832
        img.height = 624
        img.alt = data.title || ''
        container.insertBefore(img, container.firstChild)
      } catch (error) {
        console.error('Error loading Vimeo thumbnail:', error)
      }
    }

    /**
     * On click, pause all media, clone the <iframe> or <video> from the <template>, and play it.
     * @param {boolean} [focus=true]
     */
    loadContent (focus = true) {
      Tentbox.pauseAllMedia()
      if (this.hasAttribute('loaded')) return

      const template = this.querySelector('template')
      if (!template) return

      const cloned = template.content.firstElementChild.cloneNode(true)
      // If Vimeo, force autoplay
      if (cloned.classList.contains('js-vimeo')) {
        const srcUrl = new URL(cloned.src)
        srcUrl.searchParams.set('autoplay', '1')
        cloned.src = srcUrl.toString()
      }

      const wrapper = document.createElement('div')
      wrapper.appendChild(cloned)
      this.setAttribute('loaded', 'true')
      const appended = this.appendChild(wrapper.querySelector('video, model-viewer, iframe'))
      if (focus && appended) appended.focus()
    }
  }
  customElements.define('deferred-media', DeferredMedia)

  // ──────────────────────────────────────────────────────────────────────────────
  // 12. BASE VIDEO PLAYER CLASS
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Base VideoPlayer class with shared video functionality
   * Can be extended by specific video components
   */
  class VideoPlayer {
    constructor (element) {
      this.element = element
      this.videoLoaded = false
      this.isLoading = false
      this.isPlaying = false
      this.hasBeenPlayed = false
      this.wasPausedByUser = false
      this.wasPlayingBeforeDrag = false
      this.progressBar = null
      this.progressUpdateInterval = null
      this.isMuted = false
      this.controlsHideTimeout = null
      this.isDragging = false
      this.lastControlClickTime = 0
      this.shouldAutoPlayAfterLoad = false // Flag to remember user's play intent
    }

    initialize () {
      this.setupControlButton()
      this.setupMuteButton()
      this.setupFullscreenButton()
      this.setupProgressBarSeeking()
      this.progressBar = this.element.querySelector('.video-progress-bar')
      this.setupKeyboardControls()
      this.setupVideoContainerClick()

      // Restore state from attributes if available
      if (this.element.getAttribute('data-video-loaded') === 'true') {
        this.videoLoaded = true
      }
      if (this.element.getAttribute('data-has-been-played') === 'true') {
        this.hasBeenPlayed = true
      }
      if (this.element.getAttribute('data-was-paused-by-user') === 'true') {
        this.wasPausedByUser = true
      }
    }

    isMobileOrTablet () {
      return window.matchMedia('(max-width: 767px)').matches
    }

    setupControlButton () {
      const controlBtn = this.element.querySelector('.video-control-btn')
      if (controlBtn) {
        controlBtn.addEventListener('click', this.handleControlClick.bind(this))
        controlBtn.addEventListener('touchend', this.handleControlClick.bind(this), { passive: true })
      }
    }

    setupMuteButton () {
      const muteBtn = this.element.querySelector('.mute-btn')
      if (muteBtn) {
        muteBtn.addEventListener('click', this.handleMuteClick.bind(this))
      }
    }

    setupFullscreenButton () {
      const fullscreenBtn = this.element.querySelector('.fullscreen-btn')
      if (fullscreenBtn) {
        fullscreenBtn.addEventListener('click', this.handleFullscreenClick.bind(this))
      }
    }

    setupProgressBarSeeking () {
      const progressContainer = this.element.querySelector('.progress-bar-container')
      if (progressContainer) {
        progressContainer.addEventListener('click', this.handleProgressBarClick.bind(this))
        progressContainer.addEventListener('touchstart', this.handleProgressDragStart.bind(this), { passive: false })
        progressContainer.addEventListener('mousedown', this.handleProgressDragStart.bind(this))
      }
    }

    setupKeyboardControls () {
      this.element.addEventListener('keydown', this.handleKeyDown.bind(this))
    }

    setupVideoContainerClick () {
      this.element.addEventListener('click', this.handleVideoContainerClick.bind(this))
    }

    handleControlClick () {
      const now = Date.now()
      if (now - this.lastControlClickTime < 250) {
        return
      }
      this.lastControlClickTime = now

      const videoElement = this.element.querySelector('video')
      if (!videoElement) {
        return
      }

      if (!this.videoLoaded && !this.isLoading) {
        this.shouldAutoPlayAfterLoad = true // Remember user wants to play
        this.loadVideo()
      } else if (this.videoLoaded && !this.isLoading) {
        if (this.isPlaying) {
          this.pauseVideo()
        } else {
          this.playVideo()
        }
      }
    }

    loadVideo () {
      if (this.videoLoaded || this.isLoading) return

      const videoElement = this.element.querySelector('video')
      const videoSource = this.element.dataset.source

      if (videoElement && videoSource) {
        this.isLoading = true
        this.showLoadingState()

        videoElement.muted = false
        this.isMuted = false
        this.updateMuteButton()

        // Clear existing sources
        const existingSources = videoElement.querySelectorAll('source')
        existingSources.forEach(source => source.remove())

        // Determine MIME type
        let mimeType = 'video/mp4'
        if (videoSource.includes('.m3u8')) {
          mimeType = 'application/x-mpegURL'
        } else if (videoSource.includes('.webm')) {
          mimeType = 'video/webm'
        }

        const sourceElement = document.createElement('source')
        sourceElement.src = videoSource
        sourceElement.type = mimeType
        videoElement.appendChild(sourceElement)

        const handleCanPlayThrough = () => {
          this.videoLoaded = true
          this.isLoading = false
          this.element.setAttribute('data-video-loaded', 'true')
          this.showPlayState()
          this.cleanupVideoListeners(videoElement, handleCanPlayThrough, handleError)

          // Auto-play if user clicked play while loading
          if (this.shouldAutoPlayAfterLoad) {
            this.shouldAutoPlayAfterLoad = false
            setTimeout(() => {
              this.playVideo()
            }, 50) // Small delay to ensure state is properly set
          }
        }

        const handleError = (e) => {
          this.isLoading = false
          console.error('Failed to load video:', videoSource, e)
          this.cleanupVideoListeners(videoElement, handleCanPlayThrough, handleError)
          this.showPlayState()
        }

        videoElement.addEventListener('canplaythrough', handleCanPlayThrough)
        videoElement.addEventListener('error', handleError)
        videoElement.load()
      }
    }

    cleanupVideoListeners (videoElement, handleCanPlayThrough, handleError) {
      videoElement.removeEventListener('canplaythrough', handleCanPlayThrough)
      videoElement.removeEventListener('error', handleError)
    }

    playVideo () {
      const videoElement = this.element.querySelector('video')

      if (videoElement && this.videoLoaded && !this.isLoading && videoElement.readyState >= 4) {
        // Don't change state immediately - wait for play promise to resolve
        videoElement.play().then(() => {
          this.isPlaying = true
          this.hasBeenPlayed = true
          this.wasPausedByUser = false
          this.element.setAttribute('data-has-been-played', 'true')
          this.element.setAttribute('data-was-paused-by-user', 'false')
          this.showPauseState()
          this.hidePosterImage()
          this.startProgressUpdate()
          this.element.classList.remove('video-not-started')

          // Ensure video element is visible when playing
          videoElement.style.opacity = '1'
          videoElement.style.zIndex = '0'

          if (this.isMobileOrTablet()) {
            this.hideControlsWithDelay()
          }

          // Dispatch custom event for external listeners
          this.element.dispatchEvent(new CustomEvent('videoStateChange', {
            detail: { state: 'playing' },
          }))
        }).catch((error) => {
          console.error('Video play failed:', error)
          // Play failed - revert to play state without rapid flickering
          setTimeout(() => {
            this.showPlayState()
            this.element.dispatchEvent(new CustomEvent('videoStateChange', {
              detail: { state: 'ready' },
            }))
          }, 100)
        })
      } else if (!this.videoLoaded && !this.isLoading) {
        // If video isn't loaded yet, try to load it first
        this.loadVideo()
      }
    }

    pauseVideo () {
      const videoElement = this.element.querySelector('video')
      if (videoElement && !videoElement.paused) {
        videoElement.pause()
        this.isPlaying = false
        this.wasPausedByUser = true
        this.element.setAttribute('data-was-paused-by-user', 'true')
        this.showPlayState()
        this.stopProgressUpdate()

        if (this.isMobileOrTablet()) {
          if (this.controlsHideTimeout) {
            clearTimeout(this.controlsHideTimeout)
            this.controlsHideTimeout = null
          }
          this.element.classList.remove('controls-hidden')
        }

        // Dispatch custom event for external listeners
        this.element.dispatchEvent(new CustomEvent('videoStateChange', {
          detail: { state: 'paused' },
        }))
      }
    }

    handleMuteClick () {
      const videoElement = this.element.querySelector('video')
      if (videoElement) {
        this.isMuted = !this.isMuted
        videoElement.muted = this.isMuted
        this.updateMuteButton()
      }
    }

    handleFullscreenClick () {
      const videoElement = this.element.querySelector('video')
      if (!videoElement) return

      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        }
      } else {
        if (videoElement.requestFullscreen) {
          videoElement.requestFullscreen().catch(err => {
            console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`)
          })
        } else if (videoElement.webkitEnterFullscreen) {
          videoElement.webkitEnterFullscreen()
        }
      }
    }

    handleKeyDown (e) {
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault()
        this.handleControlClick()
      }

      const videoElement = this.element.querySelector('video')
      if (!videoElement || !this.videoLoaded) return

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        videoElement.currentTime = Math.max(0, videoElement.currentTime - 7)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        videoElement.currentTime = Math.min(videoElement.duration, videoElement.currentTime + 5)
      }
    }

    handleVideoContainerClick (e) {
      if (e.target.closest('.video-control-btn, .mute-btn, .fullscreen-btn, .progress-bar-container')) {
        return
      }

      if (this.isMobileOrTablet() && this.isPlaying) {
        if (!this.element.classList.contains('controls-hidden')) {
          this.element.classList.add('controls-hidden')
          if (this.controlsHideTimeout) {
            clearTimeout(this.controlsHideTimeout)
            this.controlsHideTimeout = null
          }
        } else {
          this.element.classList.remove('controls-hidden')
          this.hideControlsWithDelay()
        }
      }
    }

    hideControlsWithDelay () {
      if (this.controlsHideTimeout) {
        clearTimeout(this.controlsHideTimeout)
      }
      this.controlsHideTimeout = setTimeout(() => {
        this.element.classList.add('controls-hidden')
      }, 3000)
    }

    handleProgressBarClick (e) {
      if (this.isDragging) return

      const videoElement = this.element.querySelector('video')
      if (!videoElement || !this.videoLoaded) return

      const progressContainer = e.currentTarget
      const rect = progressContainer.getBoundingClientRect()
      const clickX = e.clientX - rect.left
      const percentage = clickX / rect.width
      const seekTime = videoElement.duration * percentage

      if (seekTime >= 0 && seekTime <= videoElement.duration) {
        videoElement.currentTime = seekTime
        this.updateProgressBar(percentage)
      }
    }

    handleProgressDragStart (e) {
      const videoElement = this.element.querySelector('video')
      if (!videoElement || !this.videoLoaded || this.isDragging) return

      this.isDragging = true
      e.preventDefault()
      e.stopPropagation()

      this.wasPlayingBeforeDrag = this.isPlaying

      if (this.isPlaying) {
        videoElement.pause()
        this.isPlaying = false
        this.stopProgressUpdate()
      }

      this.element.setAttribute('data-progress-dragging', 'true')

      const boundHandleProgressDragMove = this.handleProgressDragMove.bind(this)
      const boundHandleProgressDragEnd = this.handleProgressDragEnd.bind(this)

      document.addEventListener('touchmove', boundHandleProgressDragMove, { passive: false })
      document.addEventListener('touchend', boundHandleProgressDragEnd)
      document.addEventListener('mousemove', boundHandleProgressDragMove)
      document.addEventListener('mouseup', boundHandleProgressDragEnd)

      // Store bound functions for cleanup
      this._boundDragMove = boundHandleProgressDragMove
      this._boundDragEnd = boundHandleProgressDragEnd
    }

    handleProgressDragMove (e) {
      if (!this.isDragging) return

      const videoElement = this.element.querySelector('video')
      if (!videoElement || !this.videoLoaded) return

      e.preventDefault()
      e.stopPropagation()

      const touch = e.touches ? e.touches[0] : e
      const progressContainer = this.element.querySelector('.progress-bar-container')
      const rect = progressContainer.getBoundingClientRect()
      const x = touch.clientX - rect.left
      const percentage = Math.max(0, Math.min(1, x / rect.width))
      const seekTime = videoElement.duration * percentage

      videoElement.currentTime = seekTime
      this.updateProgressBar(percentage)
    }

    handleProgressDragEnd () {
      if (!this.isDragging) return

      this.isDragging = false
      this.element.removeAttribute('data-progress-dragging')

      // Cleanup event listeners
      if (this._boundDragMove) {
        document.removeEventListener('touchmove', this._boundDragMove)
        document.removeEventListener('mousemove', this._boundDragMove)
      }
      if (this._boundDragEnd) {
        document.removeEventListener('touchend', this._boundDragEnd)
        document.removeEventListener('mouseup', this._boundDragEnd)
      }

      if (this.wasPlayingBeforeDrag) {
        const videoElement = this.element.querySelector('video')
        if (videoElement && videoElement.paused) {
          videoElement.play().then(() => {
            this.isPlaying = true
            this.showPauseState()
            this.startProgressUpdate()
          }).catch(() => {
            this.showPlayState()
          })
        }
      }

      this.wasPlayingBeforeDrag = false
    }

    startProgressUpdate () {
      this.stopProgressUpdate()
      const videoElement = this.element.querySelector('video')
      if (!videoElement || !this.progressBar) return

      this.updateProgressBar(videoElement.currentTime / videoElement.duration)

      const update = () => {
        if (!this.progressBar) return
        if (videoElement.ended) {
          this.updateProgressBar(0)
          this.stopProgressUpdate()
          return
        }
        const duration = Number(videoElement.duration)
        const current = Number(videoElement.currentTime)
        const ratio = duration > 0 && Number.isFinite(duration) ? current / duration : 0
        this.updateProgressBar(ratio)
        this.progressUpdateInterval = requestAnimationFrame(update)
      }
      this.progressUpdateInterval = requestAnimationFrame(update)
    }

    stopProgressUpdate () {
      if (this.progressUpdateInterval) {
        cancelAnimationFrame(this.progressUpdateInterval)
        this.progressUpdateInterval = null
      }
    }

    updateProgressBar (progress) {
      if (this.progressBar) {
        this.progressBar.style.transform = `scaleX(${progress})`
      }

      if (!this.isDragging) {
        const progressHandle = this.element.querySelector('.progress-handle')
        if (progressHandle) {
          const percentage = Math.max(0, Math.min(100, progress * 100))
          progressHandle.style.left = `${percentage}%`
        }
      }
    }

    showLoadingState () {
      this.element.querySelector('.loading-icon')?.classList.remove('hidden')
      this.element.querySelector('.play-icon')?.classList.add('hidden')
      this.element.querySelector('.pause-icon')?.classList.add('hidden')

      // Dispatch loading state change
      this.element.dispatchEvent(new CustomEvent('videoStateChange', {
        detail: { state: 'loading' },
      }))
    }

    showPlayState () {
      this.element.querySelector('.loading-icon')?.classList.add('hidden')
      this.element.querySelector('.play-icon')?.classList.remove('hidden')
      this.element.querySelector('.pause-icon')?.classList.add('hidden')

      const controlBtn = this.element.querySelector('.video-control-btn')
      if (controlBtn) {
        controlBtn.setAttribute('aria-label', 'Play video')
      }

      // Dispatch play state change
      this.element.dispatchEvent(new CustomEvent('videoStateChange', {
        detail: { state: 'ready' },
      }))
    }

    showPauseState () {
      this.element.querySelector('.loading-icon')?.classList.add('hidden')
      this.element.querySelector('.play-icon')?.classList.add('hidden')
      this.element.querySelector('.pause-icon')?.classList.remove('hidden')

      const controlBtn = this.element.querySelector('.video-control-btn')
      if (controlBtn) {
        controlBtn.setAttribute('aria-label', 'Pause video')
      }
    }

    hidePosterImage () {
      const posterImg = this.element.querySelector('.video-poster')
      if (posterImg) {
        posterImg.style.opacity = '0'
        setTimeout(() => {
          if (posterImg.style.opacity === '0') {
            posterImg.style.display = 'none'
          }
        }, 300)
      }
    }

    showPosterImage () {
      const posterImg = this.element.querySelector('.video-poster')
      if (posterImg) {
        posterImg.style.display = 'block'
        posterImg.style.opacity = '1'
      }
    }

    updateMuteButton () {
      const volumeOnIcon = this.element.querySelector('.volume-on')
      const volumeOffIcon = this.element.querySelector('.volume-off')
      const muteBtn = this.element.querySelector('.mute-btn')

      if (volumeOnIcon && volumeOffIcon && muteBtn) {
        if (this.isMuted) {
          volumeOnIcon.classList.add('hidden')
          volumeOffIcon.classList.remove('hidden')
          muteBtn.setAttribute('aria-label', 'Unmute video')
        } else {
          volumeOnIcon.classList.remove('hidden')
          volumeOffIcon.classList.add('hidden')
          muteBtn.setAttribute('aria-label', 'Mute video')
        }
      }
    }

    resetVideo () {
      const videoElement = this.element.querySelector('video')
      if (videoElement) {
        videoElement.pause()
        videoElement.currentTime = 0
        // Hide video element so poster shows
        videoElement.style.opacity = '0'
        videoElement.style.zIndex = '0'

        this.isPlaying = false
        this.wasPausedByUser = false
        this.element.setAttribute('data-was-paused-by-user', 'false')
        this.showPosterImage()
        this.showPlayState()
        this.updateProgressBar(0)
        this.stopProgressUpdate()

        if (this.isMobileOrTablet()) {
          if (this.controlsHideTimeout) {
            clearTimeout(this.controlsHideTimeout)
            this.controlsHideTimeout = null
          }
          this.element.classList.remove('controls-hidden')
        }
      }
    }

    destroy () {
      this.stopProgressUpdate()
      if (this.controlsHideTimeout) {
        clearTimeout(this.controlsHideTimeout)
      }

      // Clean up drag listeners if they exist
      if (this._boundDragMove) {
        document.removeEventListener('touchmove', this._boundDragMove)
        document.removeEventListener('mousemove', this._boundDragMove)
      }
      if (this._boundDragEnd) {
        document.removeEventListener('touchend', this._boundDragEnd)
        document.removeEventListener('mouseup', this._boundDragEnd)
      }
    }
  }

  // Make VideoPlayer available globally
  Tentbox.VideoPlayer = VideoPlayer

  // ──────────────────────────────────────────────────────────────────────────────
  // 13. OBSCURE (OVERLAY) CONTROLS
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Manage two overlays (#tb_body_obscure, #tb_page_obscure) to dim/hide the page
   * when modals or flyouts are open. Provides show/hide/toggle APIs + dispatches events.
   */
  const bodyObscure = document.querySelector('#tb_body_obscure')
  const pageObscure = document.querySelector('#tb_page_obscure')

  const Obscure = {
    /**
     * Show or hide the body overlay.
     * @param {boolean} show
     */
    showBody (show) {
      if (!bodyObscure) return
      if (show) {
        bodyObscure.classList.remove('invisible', 'opacity-0')
        window.dispatchEvent(new CustomEvent('hide:klaviyo-flyout'))
      } else {
        bodyObscure.classList.add('invisible', 'opacity-0')
        window.dispatchEvent(new CustomEvent('show:klaviyo-flyout'))
      }
    },

    /**
     * Show or hide the page overlay. Hiding the body overlay ensures only one overlay visible.
     * @param {boolean} show
     */
    showPage (show) {
      if (!pageObscure) return
      const isShowing = !pageObscure.classList.contains('invisible')
      if (isShowing === show) return

      if (show) {
        pageObscure.classList.remove('invisible', 'opacity-0')
        Obscure.showBody(false)
        window.dispatchEvent(new CustomEvent('hide:mobile-menu'))
        window.dispatchEvent(new CustomEvent('hide:klaviyo-flyout'))
      } else {
        pageObscure.classList.add('invisible', 'opacity-0')
      }
    },

    /**
     * Toggle page scrolling by adding/removing a CSS class on <body>.
     * @param {boolean} allow
     */
    allowPageScroll (allow) {
      if (allow) {
        document.body.classList.remove('tb-overflow-hidden')
      } else {
        document.body.classList.add('tb-overflow-hidden')
      }
    },
  }

  // Wire up window events for obscure overlays
  window.addEventListener('show:body-obscure', () => Obscure.showBody(true))
  window.addEventListener('hide:body-obscure', () => Obscure.showBody(false))
  window.addEventListener('toggle:body-obscure', (e) => Obscure.showBody(Boolean(e.detail)))

  window.addEventListener('show:page-obscure', () => Obscure.showPage(true))
  window.addEventListener('hide:page-obscure', () => Obscure.showPage(false))
  window.addEventListener('toggle:page-obscure', (e) => Obscure.showPage(Boolean(e.detail)))

  window.addEventListener('allow:page-scroll', () => Obscure.allowPageScroll(true))
  window.addEventListener('prevent:page-scroll', () => Obscure.allowPageScroll(false))
  window.addEventListener('toggle:page-scroll', (e) => Obscure.allowPageScroll(Boolean(e.detail)))

  // Clicking on the body overlay hides it, re-enables scrolling, and hides menus
  bodyObscure?.addEventListener('click', () => {
    Obscure.showBody(false)
    Obscure.allowPageScroll(true)
    window.dispatchEvent(new CustomEvent('hide:mobile-menu'))
    window.dispatchEvent(new CustomEvent('hide:search-modal'))
  })

  pageObscure?.addEventListener('click', () => {
    const cartDrawer = document.querySelector('cart-drawer')
    if (cartDrawer) {
      cartDrawer.close()
    }
  })

  // Expose allowPageScroll globally for cart-drawer.js, etc.
  window.allowPageScroll = Obscure.allowPageScroll
  Tentbox.Obscure = Obscure

  // ──────────────────────────────────────────────────────────────────────────────
  // 13. CURRENCY UTILITIES
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Extract the first non-numeric character from a price string (e.g. “£1,234.56” → “£”).
   * @param {string} priceString
   * @returns {string}
   */
  const extractCurrencySymbol = (priceString) => {
    const match = priceString.match(/[^\d.,\s]/)
    return match ? match[0] : ''
  }

  /**
   * Parse a localized price string into a float.
   * E.g. “1.234,56” → 1234.56, “1,234.56” → 1234.56.
   * @param {string} priceString
   * @returns {number}
   */
  const parsePrice = (priceString) => {
    const cleaned = priceString.replace(/[^\d.,-]/g, '')
    const commaAfterDot = cleaned.indexOf(',') > cleaned.indexOf('.')
    const separator = commaAfterDot ? ',' : '.'
    const parts = cleaned.split(separator)
    const integerPart = parts[0].replace(/[^\d-]/g, '')
    const decimalPart = parts[1] || '00'
    return parseFloat(`${integerPart}.${decimalPart}`)
  }

  /**
   * Format a number back into a price string, preserving the original decimal separator
   * and prefixing this.currencySymbol (expects `this.priceText` and `this.currencySymbol` to exist).
   * @param {number} amount
   * @returns {string}
   */
  const formatPrice = function (amount) {
    const separator = this.priceText.includes(',') ? ',' : '.'
    const formatted = amount.toFixed(2).replace('.', separator)
    return `${this.currencySymbol}${formatted}`
  }

  Tentbox.extractCurrencySymbol = extractCurrencySymbol
  Tentbox.parsePrice = parsePrice
  Tentbox.formatPrice = formatPrice

  // ──────────────────────────────────────────────────────────────────────────────
  // 14. CROSS‐SELL VARIANT EVENTS
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Dispatch a “crosssell:variant-add” event with the given variant details.
   * @param {object} variant  { id, parentId, price, adjustedPrice }
   * @param {number} count    numberOfCheckedInputs
   */
  const dispatchVariantAddEvent = (variant, count) => {
    const payload = {
      multipleChecked: count > 1,
      numberOfCheckedInputs: count,
      variant: variant.id,
      parentId: variant.parentId,
      price: Number(variant.price * 100),
      adjustPrice: Number(variant.adjustedPrice * 100),
    }
    window.dispatchEvent(new CustomEvent('crosssell:variant-add', { detail: payload }))
  }

  /**
   * Dispatch a “crosssell:variant-remove” event.
   * @param {object} variant
   * @param {number} count
   */
  const dispatchVariantRemoveEvent = (variant, count) => {
    const payload = {
      multipleChecked: count > 1,
      numberOfCheckedInputs: count,
      variant: variant.id,
      parentId: variant.parentId,
      price: Number(variant.price * 100),
      adjustPrice: Number(variant.adjustedPrice * 100),
    }
    window.dispatchEvent(new CustomEvent('crosssell:variant-remove', { detail: payload }))
  }

  /**
   * Handler for when a “variant button” is clicked.
   * Toggles its “checked” attribute, updates the nested <input>, and fires add/remove event.
   * @param {MouseEvent} event
   * @param {object}      data    { id, parentId, price, adjustedPrice, … }
   */
  const handleVariantButtonClick = (event, data) => {
    const variantEl = event.currentTarget
    const input = variantEl.querySelector('input')
    if (!variantEl || !input) return

    const wasChecked = variantEl.getAttribute('checked') === 'true'
    variantEl.setAttribute('checked', String(!wasChecked))
    input.checked = !wasChecked

    const count = variantEl.parentElement.querySelectorAll('input:checked').length
    if (wasChecked) {
      dispatchVariantRemoveEvent(data, count)
    } else {
      dispatchVariantAddEvent(data, count)
    }
  }

  Tentbox.dispatchVariantAddEvent = dispatchVariantAddEvent
  Tentbox.dispatchVariantRemoveEvent = dispatchVariantRemoveEvent
  Tentbox.handleVariantButtonClick = handleVariantButtonClick

  // ──────────────────────────────────────────────────────────────────────────────
  // 15. CLOSE-MODAL EVENT DISPATCHER
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Dispatch a “close:modal” CustomEvent on window.
   */
  const dispatchCloseModalEvent = () => {
    window.dispatchEvent(new CustomEvent('close:modal'))
  }
  Tentbox.dispatchCloseModalEvent = dispatchCloseModalEvent

  // Expose these functions globally for product-crosssell.js onclick handlers
  window.handleVariantButtonClick = handleVariantButtonClick
  window.dispatchCloseModalEvent = dispatchCloseModalEvent

  // ──────────────────────────────────────────────────────────────────────────────
  // 16. DYNAMIC SCRIPT LOADER
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Load an external <script> by URL, ensuring it’s only fetched once.
   * Queues callbacks if the same `src` is requested while still loading.
   * @param {string}    src
   * @param {Function}  [callback]
   * @param {boolean}   [isModule=false]
   */
  const scriptsLoading = {}
  const loadScript = (src, callback, isModule = false) => {
    // If already loading, queue the callback
    if (scriptsLoading[src]) {
      scriptsLoading[src].push(callback)
      return
    }

    // If already in DOM and not currently marked as loading, call callback immediately
    if (document.querySelector(`script[src="${src}"]`) && !scriptsLoading[src]) {
      callback?.()
      return
    }

    // Mark as loading and store callbacks
    scriptsLoading[src] = [callback]

    const scriptEl = document.createElement('script')
    scriptEl.src = src
    scriptEl.type = isModule ? 'module' : 'text/javascript'
    scriptEl.onload = () => {
      scriptsLoading[src].forEach(cb => cb?.())
      delete scriptsLoading[src]
    }
    scriptEl.onerror = () => {
      console.error(`Failed to load script: ${src}`)
      scriptsLoading[src].forEach(cb => cb?.(new Error(`Failed to load script: ${src}`)))
      delete scriptsLoading[src]
    }
    document.head.appendChild(scriptEl)
  }
  Tentbox.loadScript = loadScript

  // ──────────────────────────────────────────────────────────────────────────────
  // 17. COPY-TO-CLIPBOARD CUSTOM ELEMENT
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * <copy-to-clipboard>
   * Copies `this.dataset.value` to clipboard on click, shows a temporary “✓” icon.
   */
  class CopyToClipboard extends HTMLElement {
    connectedCallback () {
      this.addEventListener('click', () => this.copy())
    }

    /**
     * Copy the value to clipboard, then show a transient “completed” icon.
     */
    copy () {
      const valueToCopy = this.value
      if (!valueToCopy) return

      const textarea = document.createElement('textarea')
      textarea.value = valueToCopy
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        this.showCompletedIcon()
      } catch (err) {
        console.error('Failed to copy text', err)
      }
      document.body.removeChild(textarea)
    }

    /**
     * Render & animate the “✓” icon (child element with class .copy-to-clipboard__completed).
     */
    showCompletedIcon () {
      const iconSVG = this.completedIcon.querySelector('svg')
      this.completedIcon.classList.remove('hidden')
      requestAnimationFrame(() => {
        this.completedIcon.classList.add('flex', 'opacity-100')
        this.classList.add('pointer-events-none')
        iconSVG.classList.add('tb-animate-scale-up')

        setTimeout(() => {
          this.completedIcon.classList.remove('opacity-100')
          setTimeout(() => {
            iconSVG.classList.remove('tb-animate-scale-up')
            this.classList.remove('pointer-events-none')
            this.completedIcon.classList.remove('flex')
            this.completedIcon.classList.add('hidden')
          }, 250)
        }, 250)
      })
    }

    get value () {
      return this.dataset.value || ''
    }

    get message () {
      return this.dataset.message || 'Copied to clipboard'
    }

    get completedIcon () {
      return this.querySelector('.copy-to-clipboard__completed')
    }
  }
  if (!customElements.get('copy-to-clipboard')) {
    customElements.define('copy-to-clipboard', CopyToClipboard)
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // 18. NOTIFICATIONS-COMPONENT CUSTOM ELEMENT
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * <notifications-component>
   * Listens for “show:notification” events and renders toast-style messages at bottom-center.
   */
  class NotificationsComponent extends HTMLElement {
    connectedCallback () {
      this.notificationDuration = 3000
      this.fadeTransitionDuration = 220

      // Create container if missing
      if (!this.querySelector('.notifications-container')) {
        const container = document.createElement('div')
        container.classList.add(
          'notifications-container', 'fixed', 'bottom-2',
          'left-1/2', 'transform', '-translate-x-1/2',
          'space-y-2', 'z-max', 'pointer-events-none',
        )
        container.setAttribute('role', 'status')
        container.setAttribute('aria-live', 'polite')
        this.appendChild(container)
      }
      this.container = this.querySelector('.notifications-container')

      window.addEventListener('show:notification', (e) => this.showNotification(e))
    }

    /**
     * Render a single notification with fade-in/out.
     * @param {CustomEvent<string>} event
     */
    showNotification (event) {
      const text = event.detail
      const notif = document.createElement('div')
      notif.classList.add(
        'flex', 'items-center', 'justify-center', 'w-fit',
        'py-3', 'px-4', 'text-white', 'text-sm', 'font-semibold',
        'bg-whatsapp', 'opacity-0', 'translate-y-4',
        'transition-opacity', 'duration-200', 'transform', 'rounded-lg',
      )
      notif.textContent = text

      this.container.appendChild(notif)

      // Trigger CSS transition
      requestAnimationFrame(() => {
        notif.classList.remove('opacity-0', 'translate-y-4')
        notif.classList.add('opacity-100', 'translate-y-0')
      })

      // After duration, fade out & remove
      setTimeout(() => {
        this.hideNotification(notif)
      }, this.notificationDuration)
    }

    /**
     * Fade out and remove a notification element.
     * @param {HTMLElement} notif
     */
    hideNotification (notif) {
      notif.classList.remove('opacity-100', 'translate-y-0')
      notif.classList.add('opacity-0', 'translate-y-4')
      setTimeout(() => {
        notif.remove()
      }, this.fadeTransitionDuration)
    }
  }
  if (!customElements.get('notifications-component')) {
    customElements.define('notifications-component', NotificationsComponent)
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // 19. SHARE-BUTTON CUSTOM ELEMENT
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * <share-button>
   * If Web Share API is available (on mobile), use it; otherwise copy URL to clipboard and display a popover.
   */
  class ShareButton extends HTMLElement {
    constructor () {
      super()
      this.shareUrl = this.dataset.shareUrl || ''
      this.button = this.querySelector('.button')
    }

    connectedCallback () {
      this.button?.addEventListener('click', (e) => this.share(e))
    }

    async share (e) {
      e.preventDefault()
      const isMobile = /Mobi|Android/i.test(navigator.userAgent)

      if (navigator.share && isMobile) {
        try {
          await navigator.share({ url: this.shareUrl })
          // success → no popover needed
        } catch (err) {
          if (err.name !== 'AbortError') {
            this.copyToClipboard()
          }
        }
      } else {
        this.copyToClipboard()
      }
    }

    copyToClipboard () {
      const textarea = document.createElement('textarea')
      textarea.value = this.shareUrl
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        this.showPopover()
      } catch (err) {
        console.error('Failed to copy URL', err)
      }
      document.body.removeChild(textarea)
    }

    showPopover () {
      this.querySelector('.share-popover')?.remove()

      const popover = document.createElement('div')
      popover.classList.add(
        'share-popover', 'absolute', 'top-full', 'right-0', 'mt-2',
        'px-3', 'py-2', 'bg-white', 'text-black',
        'border', 'border-steel', 'shadow-lg',
        'text-sm', 'rounded', 'opacity-0',
        'transition-opacity', 'whitespace-nowrap', 'z-[99999]',
      )
      popover.textContent = window.Tentbox.translations?.url_copied || 'URL copied to clipboard'

      const tail = document.createElement('span')
      tail.classList.add(
        'absolute', '-top-2', 'right-6', 'w-3', 'h-4',
        'rotate-45', 'border-l', 'border-t', 'border-steel', 'bg-white',
      )
      const srText = document.createElement('span')
      srText.classList.add('sr-only')
      srText.textContent = 'Popover tail'
      tail.appendChild(srText)

      popover.appendChild(tail)
      this.appendChild(popover)

      requestAnimationFrame(() => {
        popover.classList.add('opacity-100')
        setTimeout(() => {
          popover.classList.remove('opacity-100')
          setTimeout(() => {
            popover.remove()
          }, 200)
        }, 2000)
      })
    }
  }
  if (!customElements.get('share-button')) {
    customElements.define('share-button', ShareButton)
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // 20. KLAVIYO REVIEWS LIGHTBOX BACKDROP HANDLING
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Watch for Klaviyo reviews lightbox elements being added to the DOM,
   * and add click handler to close them when clicking the backdrop.
   */
  const bodyObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.classList && node.classList.contains('kl_reviews__lightbox_background')) {
          const klaviyoBackdrop = document.querySelector('.kl_reviews__lightbox_background')
          if (klaviyoBackdrop) {
            klaviyoBackdrop.addEventListener('click', (e) => {
              const target = e.target
              if (target !== klaviyoBackdrop) return
              const klaviyoCloseButton = document.querySelector('.kl_reviews__close_button')
              if (klaviyoCloseButton) {
                klaviyoCloseButton.click()
              }
            })
          }
        }
      })
    })
  })
  bodyObserver.observe(document.body, { childList: true })

  // ──────────────────────────────────────────────────────────────────────────────
  // 21. KLAVIYO FLYOUT HANDLING
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * Ensures that elements matching `Tentbox.selectors.marketing.klaviyoFlyout`
   * are hidden whenever the body/page overlays (“obscures”) are visible,
   * or when an explicit hide event fires.
   */
  const handleKlaviyoFlyout = () => {
    const rawSelectors = Tentbox.selectors.marketing?.klaviyoFlyout
    if (!rawSelectors) return
    const selectors = rawSelectors.split(',').map(s => s.trim()).filter(Boolean)
    if (selectors.length === 0) return

    let shouldHide = false

    // On hide:klaviyo-flyout → force-hide all matching elements
    window.addEventListener('hide:klaviyo-flyout', () => {
      shouldHide = true
      selectors.forEach(sel => {
        document.querySelectorAll(sel).forEach(el => {
          el.style.transition = 'none'
          el.style.opacity = '0'
          el.style.visibility = 'hidden'
          el.style.pointerEvents = 'none'
          el.style.zIndex = '0'
        })
      })
    })

    // On show:klaviyo-flyout → show if not flagged hidden
    window.addEventListener('show:klaviyo-flyout', () => {
      if (shouldHide) return
      selectors.forEach(sel => {
        document.querySelectorAll(sel).forEach(el => {
          el.style.transition = 'opacity 300ms ease-in-out 300ms, visibility 0ms linear 300ms'
          el.style.opacity = '1'
          el.style.visibility = 'visible'
          el.style.pointerEvents = 'auto'
          el.style.zIndex = '1'
        })
      })
    })

    // Observe changes on overlays; if either overlay is visible, hide flyout
    const bodyIsVisible = () => Boolean(bodyObscure && !bodyObscure.classList.contains('invisible'))
    const pageIsVisible = () => Boolean(pageObscure && !pageObscure.classList.contains('invisible'))

    const obscureObserver = new MutationObserver(() => {
      const nowHide = bodyIsVisible() || pageIsVisible()
      if (nowHide && !shouldHide) {
        shouldHide = true
        selectors.forEach(sel => {
          document.querySelectorAll(sel).forEach(el => {
            el.style.transition = 'none'
            el.style.opacity = '0'
            el.style.visibility = 'hidden'
            el.style.pointerEvents = 'none'
          })
        })
      } else if (!nowHide && shouldHide) {
        shouldHide = false
        window.dispatchEvent(new CustomEvent('show:klaviyo-flyout'))
      }
    })

    if (bodyObscure) {
      obscureObserver.observe(bodyObscure, { attributes: true, attributeFilter: ['class'] })
    }
    if (pageObscure) {
      obscureObserver.observe(pageObscure, { attributes: true, attributeFilter: ['class'] })
    }
  }
  handleKlaviyoFlyout()

  // ──────────────────────────────────────────────────────────────────────────────
  // 22. TENTBOX-CARD CUSTOM ELEMENT
  // ──────────────────────────────────────────────────────────────────────────────
  /**
   * <tentbox-card>
   * Product card component with finance calculations.
   * Uses the same compound interest formula as the PDP for accurate finance calculations.
   */
  class TentboxCard extends HTMLElement {
    connectedCallback () {
      this.priceElement = this.querySelector('.tentbox-card__price-container')
      this.initFinanceCalculation()
    }

    initFinanceCalculation () {
      // Initialize finance calculation for the default variant
      const financeElement = this.querySelector('.product-card-finance__from-price')
      if (financeElement && this.priceElement) {
        const priceElement = this.priceElement.querySelector('.font-medium:not(.line-through)')
        if (priceElement) {
          const priceText = priceElement.textContent
          const priceCents = this.parsePriceFromText(priceText)
          if (priceCents) {
            this.updateFinanceDisplay(priceCents, financeElement)
          }
        }
      }
    }

    parsePriceFromText (priceText) {
      if (!priceText) return null
      // Remove currency symbols and parse the price
      const cleanedPrice = priceText.replace(/[€£$¥₹¢,\s]/g, '')
      const price = parseFloat(cleanedPrice)
      return isNaN(price) ? null : Math.round(price * 100) // Convert to cents
    }

    updateFinanceDisplay (variantPriceCents, financeElement) {
      const minPrice = parseInt(financeElement.dataset.minPrice, 10) || 5000

      // Only show finance if price meets minimum threshold
      if (variantPriceCents < minPrice) {
        financeElement.style.display = 'none'
        return
      }

      // Get finance terms from element dataset and parse from string
      const financeTranslation = financeElement.dataset.financeTranslation
      const financeTermsData = financeElement.dataset.financeTerms
      const financeTerms = this.parseFinanceTerms(financeTermsData)

      if (!financeTerms || financeTerms.length === 0 || !financeTranslation) {
        financeElement.style.display = 'none'
        return
      }

      // Calculate the minimum monthly payment (longest term) with rounding up
      const monthlyPayment = this.calculateMinMonthlyPayment(variantPriceCents, financeTerms)

      if (monthlyPayment > 0) {
        // Round up to whole currency unit for product cards
        const roundedUpPayment = Math.ceil(monthlyPayment)
        const formattedMonthlyPayment = this.formatFinanceAmount(roundedUpPayment)
        const finalText = financeTranslation.replace('[[PRICE]]', formattedMonthlyPayment)
        financeElement.textContent = finalText
        financeElement.style.display = ''
      } else {
        financeElement.style.display = 'none'
      }
    }

    formatFinanceAmount (amount) {
      // Simple format for finance amounts - detect currency from existing price on page
      const existingPriceElement = this.priceElement.querySelector('.font-medium')
      if (existingPriceElement) {
        const existingText = existingPriceElement.textContent || ''
        const currencyMatch = existingText.match(/[€£$¥₹¢]/g)
        const currencySymbol = currencyMatch ? currencyMatch[0] : '£'

        // For finance amounts, use simple format: £65, €75, $80
        return `${currencySymbol}${amount}`
      }

      // Fallback to £ for finance amounts
      return `£${amount}`
    }

    parseFinanceTerms (data) {
      const terms = []
      if (!data) return terms

      const termStrings = data.split(',')
      termStrings.forEach((termStr) => {
        const [title, apr, period] = termStr.split('|')
        if (title && apr && period) {
          terms.push({
            title: title.trim(),
            apr: parseFloat(apr),
            period: parseInt(period, 10),
          })
        }
      })
      return terms
    }

    calculateMinMonthlyPayment (priceCents, financeTerms) {
      // Find the longest term (lowest monthly payment)
      let longestTerm = null
      let maxPeriod = 0

      financeTerms.forEach((term) => {
        if (term.period > maxPeriod) {
          maxPeriod = term.period
          longestTerm = term
        }
      })

      if (!longestTerm) return 0

      const priceInPounds = priceCents / 100
      const PT = longestTerm.period
      const AIR = longestTerm.apr

      let monthlyPayment

      if (AIR === 0) {
        // 0% APR - simple division
        monthlyPayment = priceInPounds / PT
      } else {
        // Calculate with compound interest - same formula as PDP
        const PIR = Math.pow(1 + AIR / 100, 1 / 12) - 1
        const onePlusPIRToPT = Math.pow(1 + PIR, PT)
        const numerator = priceInPounds * PIR * onePlusPIRToPT
        const denominator = onePlusPIRToPT - 1
        monthlyPayment = numerator / denominator
      }

      return monthlyPayment // Return exact amount, rounding handled in updateFinanceDisplay
    }
  }

  if (!customElements.get('tentbox-card')) {
    customElements.define('tentbox-card', TentboxCard)
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // 23. FILTER BUS + SECTION FETCH HELPERS (for collection filters)
  // ──────────────────────────────────────────────────────────────────────────────
  const filterBus = (() => {
    const eventNameChange = 'filters:change'
    const eventNameLoading = 'filters:loading'
    const eventNameLoaded = 'filters:loaded'
    return {
      onChange (cb) { window.addEventListener(eventNameChange, cb) },
      offChange (cb) { window.removeEventListener(eventNameChange, cb) },
      emitChange (detail) { window.dispatchEvent(new CustomEvent(eventNameChange, { detail })) },
      emitLoading (detail) { window.dispatchEvent(new CustomEvent(eventNameLoading, { detail })) },
      emitLoaded (detail) { window.dispatchEvent(new CustomEvent(eventNameLoaded, { detail })) },
    }
  })()
  window.filterBus = filterBus

  const filterFetch = {
    async fetchSections (sectionIds, url) {
      try {
        const base = new URL(url, window.location.origin)
        const params = new URLSearchParams(base.search)
        params.set('sections', sectionIds.join(','))
        const fetchUrl = `${base.pathname}?${params.toString()}`

        const res = await fetch(fetchUrl, { headers: { Accept: 'application/json' } })
        const contentType = res.headers.get('content-type') || ''
        if (!res.ok) throw new Error(`Failed to fetch sections: ${res.status}`)

        let mapping = null
        if (contentType.includes('application/json')) {
          const json = await res.json()

          if (sectionIds.every(id => json && Object.prototype.hasOwnProperty.call(json, id))) {
            mapping = json
          } else if (json && json.sections && typeof json.sections === 'object') {
            mapping = json.sections
          }

          if (mapping) {
            return mapping
          }
        }

        const htmlRes = await fetch(url, { headers: { Accept: 'text/html' } })
        if (!htmlRes.ok) throw new Error(`HTML fetch failed: ${htmlRes.status}`)
        const html = await htmlRes.text()
        const doc = new DOMParser().parseFromString(html, 'text/html')
        const result = {}
        sectionIds.forEach((id) => {
          const el = doc.getElementById(`shopify-section-${id}`)
          if (el) result[id] = el.innerHTML
        })
        return result
      } catch (e) {
        console.error(e)
        return null
      }
    },
  }
  window.filterFetch = filterFetch

  // ──────────────────────────────────────────────────────────────────────────────
  // End of IIFE
  // ──────────────────────────────────────────────────────────────────────────────

  const htmlElement = document.documentElement
  setTimeout(() => {
    htmlElement.style.maxWidth = '100vw'
    htmlElement.style.overflowX = 'initial'
  }, 1000)
})()
